import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents } from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import { useMobileNav } from '../../contexts/MobileNavContext';
import api from '../../api';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash-outline.svg';
import remindersAppIcon from '../../icons/app-icon-reminders.png';
import owedRepliesAppIcon from '../../icons/app-icon-owed-replies.png';
import documentsAppIcon from '../../icons/app-icon-documents.png';
import tripsAppIcon from '../../icons/app-icon-trips.png';
import keyDatesAppIcon from '../../icons/app-icon-key-dates.png';
import generatingAppIcon from '../../icons/app-icon-generating.svg';
import { ReactComponent as CheckSquareIcon } from '../../icons/check-square.svg';
import { ReactComponent as FlipBackIcon } from '../../icons/flip-back.svg';
import { ReactComponent as FileIcon } from '../../icons/file-05.svg';
import { ReactComponent as HomeFileIcon } from '../../icons/home/file-06.svg';
import { ReactComponent as HomeChevronIcon } from '../../icons/home/chevron-right.svg';
import { ReactComponent as CalendarHeartIcon } from '../../icons/calendar-heart-02.svg';
import { ReactComponent as LuggageIcon } from '../../icons/luggage-02.svg';
import MobileAppHeader from './mobile-apps/MobileAppHeader';
import MobileAppMenu, { MobileAppMenuEntry } from './mobile-apps/MobileAppMenu';
import ProjectAppGeneratedPage from './canvas/ProjectAppGeneratedPage';
import { ProjectAppLoadingProvider } from '../../providers/ProjectAppViewLoadingProvider';
import RemindersPage from './RemindersPage';
import OwedRepliesPage from './OwedRepliesPage';
import DocumentsPage from './DocumentsPage';
import ConfirmModal from '../confirm-modals/ConfirmModal';
import { useAppPwaManifest } from '../../hooks/useAppPwaManifest';
import './global-apps-section.scss';

/**
 * "Global Apps" home-page carousel.
 *
 * Renders an app carousel that combines:
 *   - Default "fixed" apps that ship with every account (Reminders, Documents)
 *     which click through to the existing Reminders / Documents views.
 *   - The provisioned routine-backed global apps (Trips, Key Dates), which
 *     render via the project-app iframe renderer (ProjectAppGeneratedPage).
 *     Other user-generated apps are not surfaced.
 *
 * Selecting any app opens it inside the shared app-view layout: a Back button,
 * an app header (icon + title), then the app content below.
 */

// ── Backend (routine-backed) global apps ──
interface GlobalApp {
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  homeViewSlug: string | null;
  iconUrl: string | null;
  updatedAt: string | null;
}

/**
 * Default "fixed" apps that ship with every account. Reminders and Documents
 * are not routine-backed — selecting them simply opens the existing Reminders
 * / Documents view inside the shared app-view layout (Back + header + content).
 */
type DefaultAppType = 'reminders' | 'documents' | 'owed-replies';

interface DefaultApp {
  kind: 'default';
  type: DefaultAppType;
  name: string;
  icon: string;
}

interface BackendAppItem extends GlobalApp {
  kind: 'global';
}

type AppItem = DefaultApp | BackendAppItem;

/** Header content an open app's page reports up to the app-view shell. */
interface AppHeaderMeta {
  subtitle: string | null;
  titleCount: number | null;
  actions: React.ReactNode | null;
}

const EMPTY_APP_HEADER_META: AppHeaderMeta = {
  subtitle: null,
  titleCount: null,
  actions: null,
};

/**
 * Icons for backend global apps: a generated icon (iconUrl) when available,
 * bundled icons for provisioned defaults like Trips, otherwise a placeholder
 * shown while the icon is still generating (or generation failed).
 */
const backendAppIcon = (app: GlobalApp): string => {
  if (app.iconUrl) return app.iconUrl;
  if (app.slug === 'trips' || app.name === 'Trips') return tripsAppIcon;
  if (app.slug === 'key-dates' || app.name === 'Key Dates')
    return keyDatesAppIcon;
  return generatingAppIcon;
};

/**
 * The animated generating placeholder, isolated in a memoized component with
 * no props so React never re-renders it — the <img> element (and its SVG
 * animation) is left untouched no matter how often the carousel re-renders.
 */
const GeneratingIcon = React.memo(() => <img src={generatingAppIcon} alt='' />);
GeneratingIcon.displayName = 'GeneratingIcon';

const BackendAppIcon: React.FC<{ app: GlobalApp }> = ({ app }) =>
  app.iconUrl ||
  app.slug === 'trips' ||
  app.name === 'Trips' ||
  app.slug === 'key-dates' ||
  app.name === 'Key Dates' ? (
    <img src={backendAppIcon(app)} alt='' />
  ) : (
    <GeneratingIcon />
  );

/**
 * Line icons for the mobile apps menu (Figma 5227:17436). Backend apps other
 * than the provisioned Trips / Key Dates have none and are listed under
 * "Your apps".
 */
const DEFAULT_APP_MENU_ICONS: Record<DefaultAppType, React.ReactNode> = {
  reminders: <CheckSquareIcon />,
  'owed-replies': <FlipBackIcon />,
  documents: <FileIcon />,
};

/** 32px / 2px-stroke icons for the desktop home link rows (Figma Dream 4.0 Home). */
const HOME_LINK_ICONS: Partial<Record<DefaultAppType, React.ReactNode>> = {
  documents: <HomeFileIcon />,
};

const backendAppMenuIcon = (app: GlobalApp): React.ReactNode => {
  if (app.slug === 'trips' || app.name === 'Trips') return <LuggageIcon />;
  if (app.slug === 'key-dates' || app.name === 'Key Dates')
    return <CalendarHeartIcon />;
  return undefined;
};

const DEFAULT_APPS: DefaultApp[] = [
  {
    kind: 'default',
    type: 'reminders',
    name: 'Reminders',
    icon: remindersAppIcon,
  },
  {
    kind: 'default',
    type: 'owed-replies',
    name: 'Owed replies',
    icon: owedRepliesAppIcon,
  },
  {
    kind: 'default',
    type: 'documents',
    name: 'Documents',
    icon: documentsAppIcon,
  },
];

interface GlobalAppsSectionProps {
  /**
   * Notifies the parent when an app is opened/closed so the rest of the home
   * view (greeting, long-term work) can be hidden while an app takes over the
   * left panel.
   */
  onAppOpenChange?: (open: boolean) => void;
  /**
   * Number of outstanding reminders — rendered as a notification badge on the
   * Reminders app tile. Hidden when zero.
   */
  remindersCount?: number;
  /**
   * Number of open followup items — rendered as a notification badge on the
   * Owed replies app tile. Hidden when zero.
   */
  owedRepliesCount?: number;
  /**
   * `grid` (default) is the icon tile grid. `links` is the compact row list
   * used under the desktop Schedule/Inbox/Lists panels: it lists only what
   * those panels don't already cover (Documents), plus `extraLinks` first.
   * `none` renders nothing while no app is open (the host is showing a page
   * that replaces the home stack) but keeps the URL-driven app views alive.
   */
  presentation?: 'grid' | 'links' | 'none';
  /** Host-owned destinations listed ahead of the apps in `links` mode. */
  extraLinks?: HomeLink[];
  /** Host-owned destinations listed after the built-in apps in `links` mode. */
  trailingLinks?: HomeLink[];
}

export interface HomeLink {
  id: string;
  name: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

const isProvisionedApp = (app: GlobalApp): boolean =>
  app.slug === 'trips' ||
  app.name === 'Trips' ||
  app.slug === 'key-dates' ||
  app.name === 'Key Dates';

// Notification badges render "99+" once the count passes 99 to keep the pill compact.
const formatBadgeCount = (count: number): string =>
  count > 99 ? '99+' : String(count);

const GlobalAppsSection: React.FC<GlobalAppsSectionProps> = ({
  onAppOpenChange,
  remindersCount = 0,
  owedRepliesCount = 0,
  presentation = 'grid',
  extraLinks = [],
  trailingLinks = [],
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isMobile } = useMobileNav();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  // Inbox Manager now lives under Settings; keep old app links working.
  useEffect(() => {
    if (searchParams.get('app_tab') === 'inbox-management') {
      navigate('/settings/inbox-manager', { replace: true });
    }
  }, [searchParams, navigate]);

  const [apps, setApps] = useState<GlobalApp[]>([]);
  const [selected, setSelected] = useState<AppItem | null>(null);
  // Header content (subtitle, title count, actions) supplied by the open
  // app's page. Tagged with the app it was reported for so nothing stale leaks
  // between apps, without a reset effect that would race a page reporting on
  // mount.
  const [appHeader, setAppHeader] = useState<{
    app: AppItem | null;
    meta: AppHeaderMeta;
  }>({ app: null, meta: EMPTY_APP_HEADER_META });
  const patchAppHeader = useCallback(
    (patch: Partial<AppHeaderMeta>) =>
      setAppHeader((prev) => ({
        app: selected,
        meta: {
          ...(prev.app === selected ? prev.meta : EMPTY_APP_HEADER_META),
          ...patch,
        },
      })),
    [selected],
  );
  const setAppSubtitle = useCallback(
    (subtitle: string | null) => patchAppHeader({ subtitle }),
    [patchAppHeader],
  );
  const setAppTitleCount = useCallback(
    (titleCount: number | null) => patchAppHeader({ titleCount }),
    [patchAppHeader],
  );
  const setAppActions = useCallback(
    (actions: React.ReactNode | null) => patchAppHeader({ actions }),
    [patchAppHeader],
  );
  const {
    subtitle: appSubtitle,
    titleCount: appTitleCount,
    actions: appActions,
  } = appHeader.app === selected ? appHeader.meta : EMPTY_APP_HEADER_META;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // On mobile the top-level `?tab=reminders|documents` routes also open the
  // app here (UniPage hands them over) so both entry points share the shell.
  const defaultAppForTab = useMemo(() => {
    const appTab =
      searchParams.get('app_tab') ??
      (isMobile ? searchParams.get('tab') : null);
    return DEFAULT_APPS.find((d) => d.type === appTab) ?? null;
  }, [searchParams, isMobile]);

  // Resolve the open app from the URL: app_tab selects a default app (used by
  // chat links like InlineResponseArtifact), app_id a backend global app (used
  // by per-app PWA installs and shareable links). app_tab takes precedence so
  // a leftover app_id can't compete for the selection, and the update is
  // functional so `selected` stays out of the dependencies — otherwise the two
  // deep links overwrite each other on every render.
  useEffect(() => {
    if (defaultAppForTab) {
      setSelected((prev) =>
        prev?.kind === 'default' && prev.type === defaultAppForTab.type
          ? prev
          : defaultAppForTab,
      );
      return;
    }
    const appId = searchParams.get('app_id');
    if (!appId) {
      // A default app selected purely via the mobile `tab` hand-over must close
      // once the viewport is desktop, where UniPage renders that tab directly.
      const urlTab = searchParams.get('tab');
      if (urlTab) {
        setSelected((prev) =>
          prev?.kind === 'default' && prev.type === urlTab ? null : prev,
        );
      }
      return;
    }
    const app = apps.find((a) => a.id === appId);
    if (!app) return;
    setSelected((prev) =>
      prev?.kind === 'global' && prev.id === appId
        ? prev
        : { ...app, kind: 'global' },
    );
  }, [defaultAppForTab, searchParams, apps]);

  useEffect(() => {
    onAppOpenChange?.(!!selected);
  }, [selected, onAppOpenChange]);

  const fetchApps = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{ success: boolean; apps: GlobalApp[] }>(
        '/global-apps',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        // Only update state when the render-relevant fields changed — a
        // re-render restarts the placeholder SVG's animation, making it
        // flicker on every poll. Volatile fields (updatedAt churns on every
        // routine write) are excluded from the comparison.
        const renderKey = (list: GlobalApp[]) =>
          JSON.stringify(
            list.map(
              ({ id, name, description, slug, homeViewSlug, iconUrl }) => ({
                id,
                name,
                description,
                slug,
                homeViewSlug,
                iconUrl,
              }),
            ),
          );
        const visible = res.data.apps.filter(isProvisionedApp);
        setApps((prev) =>
          renderKey(prev) === renderKey(visible) ? prev : visible,
        );
      }
    } catch (err) {
      console.error('Failed to load global apps:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  // Refetch live when the backend announces a new global app or a freshly
  // generated icon, so tiles appear/update without a manual refresh.
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  useEffect(() => {
    if (!userChannel) return;

    const refresh = () => {
      fetchApps();
    };

    userChannel.bind(AgentEvents.GLOBAL_APP_CREATED, refresh);
    userChannel.bind(AgentEvents.GLOBAL_APP_ICON_UPDATED, refresh);
    return () => {
      userChannel.unbind(AgentEvents.GLOBAL_APP_CREATED, refresh);
      userChannel.unbind(AgentEvents.GLOBAL_APP_ICON_UPDATED, refresh);
    };
  }, [userChannel, fetchApps]);

  // While any app is missing its (background-generated) icon, poll so the
  // real icon replaces the placeholder as soon as it is ready.
  const awaitingIcons = apps.some((app) => !app.iconUrl);
  useEffect(() => {
    if (!awaitingIcons) return;
    const timer = setInterval(fetchApps, 15000);
    return () => clearInterval(timer);
  }, [awaitingIcons, fetchApps]);

  const handleSelectApp = useCallback(
    (app: AppItem) => {
      setSelected(app);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('research_app_id');
          next.delete('tab');
          if (app.kind === 'default') {
            next.set('app_tab', app.type);
            next.delete('app_id');
          } else {
            next.delete('app_tab');
            next.set('app_id', app.id);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleBack = useCallback(() => {
    setSelected(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('app_tab');
        next.delete('app_id');
        next.delete('research_app_id');
        next.delete('tab');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleDeleteApp = useCallback(async () => {
    if (!selected || selected.kind !== 'global') return;

    setIsDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.delete(`/global-apps/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setShowDeleteModal(false);
      setSelected(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('app_tab');
          next.delete('app_id');
          next.delete('research_app_id');
          return next;
        },
        { replace: true },
      );
      fetchApps();
    } catch (err) {
      console.error('Failed to delete global app:', err);
      window.alert('Could not delete the app. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [selected, getAccessTokenSilently, setSearchParams, fetchApps]);

  const items: AppItem[] = [
    ...DEFAULT_APPS,
    ...apps.map((app): BackendAppItem => ({ ...app, kind: 'global' })),
  ];

  // Notification count shown on an app tile (top-right badge), sourced from
  // counts computed by the host.
  const appBadgeCount = (app: AppItem): number => {
    if (app.kind !== 'default') return 0;
    if (app.type === 'reminders') return remindersCount;
    if (app.type === 'owed-replies') return owedRepliesCount;
    return 0;
  };

  // While an app is open, swap the site manifest for a per-app one so "Add
  // to Home Screen" installs this app with its own name, icon and deep link.
  useAppPwaManifest(
    selected
      ? {
          name: selected.name,
          iconUrl:
            selected.kind === 'default'
              ? selected.icon
              : backendAppIcon(selected),
          startUrl:
            selected.kind === 'default'
              ? `/?app_tab=${selected.type}`
              : `/?app_id=${selected.id}`,
        }
      : null,
  );

  // ── Selected: render the shared app-view layout ──
  if (selected) {
    const isSelected = (app: AppItem): boolean =>
      app.kind === 'default'
        ? selected.kind === 'default' && selected.type === app.type
        : selected.kind === 'global' && selected.id === app.id;

    const mobileMenuEntries: MobileAppMenuEntry[] = items.map((app) => ({
      id: app.kind === 'default' ? app.type : app.id,
      name: app.name,
      icon:
        app.kind === 'default'
          ? DEFAULT_APP_MENU_ICONS[app.type]
          : backendAppMenuIcon(app),
      active: isSelected(app),
      onSelect: () => handleSelectApp(app),
    }));

    const deleteButton = selected.kind === 'global' && (
      <button
        type='button'
        className='app-view-delete'
        onClick={() => setShowDeleteModal(true)}
        aria-label='Delete app'
      >
        <TrashIcon />
        <span className='app-view-delete-label'>Delete app</span>
      </button>
    );

    return (
      <section
        className={`global-apps-section${
          isMobile ? ' global-apps-section--mobile-app' : ''
        }`}
      >
        <div className='app-view'>
          {isMobile ? (
            <>
              <MobileAppHeader
                title={selected.name}
                onOpenMenu={() => setMobileMenuOpen(true)}
                actions={deleteButton}
              />
              <MobileAppMenu
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                entries={mobileMenuEntries}
                onHome={handleBack}
              />
            </>
          ) : (
            <>
              <button
                type='button'
                className='app-view-back'
                onClick={handleBack}
              >
                <ArrowLeftIcon />
                <span>Back</span>
              </button>

              <header className='app-view-header'>
                <div className='app-view-heading'>
                  <h1 className='app-view-title'>
                    {selected.name}
                    {appTitleCount !== null && appTitleCount > 0 && (
                      <span className='app-view-title-count'>
                        {appTitleCount}
                      </span>
                    )}
                  </h1>
                  {appSubtitle && (
                    <div className='app-view-subtitle'>{appSubtitle}</div>
                  )}
                </div>
                {(appActions || deleteButton) && (
                  <div className='app-view-actions'>
                    {appActions}
                    {deleteButton}
                  </div>
                )}
              </header>
            </>
          )}

          <div
            className={`app-view-body${
              selected.kind === 'global' ? ' app-view-body--frame' : ''
            }`}
          >
            {selected.kind === 'default' && selected.type === 'reminders' && (
              <RemindersPage hideHeading onSubtitleChange={setAppSubtitle} />
            )}
            {selected.kind === 'default' &&
              selected.type === 'owed-replies' && (
                <OwedRepliesPage
                  hideHeading
                  onSubtitleChange={setAppSubtitle}
                  onTitleCountChange={setAppTitleCount}
                  onActionsChange={setAppActions}
                />
              )}
            {selected.kind === 'default' && selected.type === 'documents' && (
              <DocumentsPage hideHeading onSubtitleChange={setAppSubtitle} />
            )}
            {selected.kind === 'global' &&
              (() => {
                const slug = selected.homeViewSlug || selected.slug;
                // Keyed per app: each app gets its own page state.
                return slug ? (
                  <ProjectAppLoadingProvider key={selected.id}>
                    <ProjectAppGeneratedPage
                      appId={selected.id}
                      slug={slug}
                      showViewTabs
                    />
                  </ProjectAppLoadingProvider>
                ) : (
                  <p className='global-apps-section-empty'>
                    This app has no view yet — its routine is still preparing
                    it.
                  </p>
                );
              })()}
          </div>
        </div>

        {selected.kind === 'global' && (
          <ConfirmModal
            show={showDeleteModal}
            handleClose={() => setShowDeleteModal(false)}
            handleConfirm={handleDeleteApp}
            title='Delete app'
            bodyText={`Deleting “${selected.name}” will remove all of its data and cannot be undone.`}
            confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
            cancelLabel='Cancel'
            confirmDisabled={isDeleting}
            singleButton={false}
            verticalButtons={false}
            littleSweetieVersion={false}
            confirmFirst={false}
          />
        )}
      </section>
    );
  }

  // ── Mobile: glass app grid (Figma 5135:61110) ──
  if (isMobile) {
    return (
      <section className='global-apps-section'>
        <div className='mobile-apps-glass'>
          <div className='global-apps-carousel mobile-apps-grid' role='list'>
            {items.map((app) => {
              const key = app.kind === 'default' ? app.type : app.id;
              const badgeCount = appBadgeCount(app);
              return (
                <button
                  key={key}
                  type='button'
                  className='global-app mobile-app'
                  onClick={() => handleSelectApp(app)}
                >
                  <div className='global-app-icon'>
                    <div className='global-app-icon-plate'>
                      {app.kind === 'default' ? (
                        <img src={app.icon} alt='' />
                      ) : (
                        <BackendAppIcon app={app} />
                      )}
                    </div>
                    {badgeCount > 0 && (
                      <span
                        className='global-app-badge'
                        aria-label={`${badgeCount} notifications`}
                      >
                        {formatBadgeCount(badgeCount)}
                      </span>
                    )}
                  </div>
                  <span className='global-app-label mobile-app-label'>
                    {app.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (presentation === 'none') return null;

  // ── Desktop home: row links for what the Schedule/Inbox/Lists panels don't cover ──
  if (presentation === 'links') {
    const appLink = (app: AppItem): HomeLink => ({
      id: app.kind === 'default' ? app.type : app.id,
      name: app.name,
      icon:
        app.kind === 'default'
          ? (HOME_LINK_ICONS[app.type] ?? DEFAULT_APP_MENU_ICONS[app.type])
          : (backendAppMenuIcon(app) ?? <BackendAppIcon app={app} />),
      onSelect: () => handleSelectApp(app),
    });
    const links: HomeLink[] = [
      ...extraLinks,
      ...items
        .filter((app) => app.kind === 'default' && app.type === 'documents')
        .map(appLink),
      ...trailingLinks,
    ];
    return (
      <section
        className='global-apps-section global-apps-section--links'
        aria-label='More'
      >
        {links.map((link) => (
          <button
            key={link.id}
            type='button'
            className='home-link'
            onClick={link.onSelect}
          >
            <span className='home-link-icon' aria-hidden='true'>
              {link.icon}
            </span>
            <span className='home-link-name'>{link.name}</span>
            <HomeChevronIcon className='home-link-chevron' />
          </button>
        ))}
      </section>
    );
  }

  // ── Default: app grid ──
  return (
    <section className='global-apps-section'>
      <div className='global-apps-carousel'>
        {items.map((app) => {
          const key = app.kind === 'default' ? app.type : app.id;
          const badgeCount = appBadgeCount(app);
          return (
            <button
              key={key}
              type='button'
              className='global-app'
              onClick={() => handleSelectApp(app)}
            >
              <div className='global-app-icon'>
                <div className='global-app-icon-plate'>
                  {app.kind === 'default' ? (
                    <img src={app.icon} alt='' />
                  ) : (
                    <BackendAppIcon app={app} />
                  )}
                </div>
                {badgeCount > 0 && (
                  <span
                    className='global-app-badge'
                    aria-label={`${badgeCount} notifications`}
                  >
                    {formatBadgeCount(badgeCount)}
                  </span>
                )}
              </div>
              <span className='global-app-label'>{app.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default GlobalAppsSection;
